/**
 * Soundcharts (del B, sak 273): artistpanelets Publikum, Konserter og plattformraden, og Control Room ›
 * Soundcharts.
 *
 * `GET /v1/booking/artists/:artistId/soundcharts` — artistId er organisasjonens rad (også en virtuell
 * id fra den offisielle lista). Tallene hører til den offisielle artisten og er offentlige (29. sep):
 * alle med tilgang til registeret leser dem. Et tall vi ikke har, er null, aldri 0.
 *
 * `/v1/platform/soundcharts/*` — bare for plattformadmin; alle andre får 404.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

/** Datatypene i køen, i den rekkefølgen Control Room viser dem. Samme liste som `app.sc_data_types()`. */
export const SOUNDCHARTS_DATA_TYPES = [
  'id_link',
  'metadata',
  'identifiers',
  'spotify_listeners',
  'spotify_cities',
  'followers_spotify',
  'followers_instagram',
  'followers_tiktok',
  'followers_youtube',
  'demographics_instagram',
  'demographics_tiktok',
  'demographics_youtube',
  'events',
] as const;
export type SoundchartsDataType = (typeof SOUNDCHARTS_DATA_TYPES)[number];

/** Det som kan låses i Control Room: en datatype, eller opplåsingen av publikumsrapporter. */
export const SOUNDCHARTS_LOCKABLE = [...SOUNDCHARTS_DATA_TYPES, 'unlock'] as const;
export type SoundchartsLockable = (typeof SOUNDCHARTS_LOCKABLE)[number];

export const SOUNDCHARTS_PLATFORMS = ['spotify', 'instagram', 'tiktok', 'youtube'] as const;
export type SoundchartsPlatform = (typeof SOUNDCHARTS_PLATFORMS)[number];

export const SOUNDCHARTS_REPORT_PLATFORMS = ['instagram', 'tiktok', 'youtube'] as const;
export type SoundchartsReportPlatform = (typeof SOUNDCHARTS_REPORT_PLATFORMS)[number];

export const SOUNDCHARTS_DEMOGRAPHICS_STATUSES = [
  'ok',
  'not_available',
  'locked',
  'pending',
] as const;
export type SoundchartsDemographicsStatus = (typeof SOUNDCHARTS_DEMOGRAPHICS_STATUSES)[number];

export const SOUNDCHARTS_FETCH_STATUSES = ['ok', 'error', 'not_available', 'locked'] as const;
export type SoundchartsFetchStatus = (typeof SOUNDCHARTS_FETCH_STATUSES)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// ------------------------------------------------------------------------------- artistpanelet

const changeSchema = z.object({
  abs: z.number(),
  /** Null når utgangspunktet var 0. */
  pct: z.number().nullable(),
  /** Dagene endringen faktisk spenner over: 27–29 for «30 dager» når serien er litt kortere. */
  days: z.number().int(),
});

const platformStatSchema = z.object({
  /** Profilen: lenken organisasjonen har satt, ellers den offisielle, ellers Soundcharts'. */
  url: z.string().nullable(),
  /** Spotify: månedlige lyttere. De andre: følgere. */
  value: z.number().nullable(),
  /** Lenken satt for hånd (organisasjonen eller den offisielle lista), for redigeringsfeltet. */
  manualUrl: z.string().nullable(),
});

const shareSchema = z.object({ pct: z.number() });
const ageShareSchema = shareSchema.extend({ group: z.string() });
const genderShareSchema = shareSchema.extend({ gender: z.string() });
/** Land (ISO 3166-1 alfa-2) fra rapporten; `name` er kildens engelske navn. */
const codeShareSchema = shareSchema.extend({ code: z.string(), name: z.string().nullable() });

const demographicsPlatformSchema = z.object({
  status: z.enum(SOUNDCHARTS_DEMOGRAPHICS_STATUSES),
  reportDate: isoDate.nullable(),
  /** Rapportens eget følgertall (avrundet for TikTok og YouTube). Følgerkortene bruker serien. */
  followers: z.number().nullable(),
  ages: z.array(ageShareSchema),
  genders: z.array(genderShareSchema),
  /** Andel ekte følgere, 0–1. */
  credibility: z.number().nullable(),
  /** Topp 10 land, størst først. Språk og interesser vises ikke (Atle, 24. okt). */
  countries: z.array(codeShareSchema),
});

const followerStatSchema = z
  .object({ current: z.number().nullable(), change30d: changeSchema.nullable() })
  .nullable();

export const soundchartsEventSchema = z.object({
  date: isoDate,
  name: z.string().nullable(),
  venueName: z.string().nullable(),
  festivalName: z.string().nullable(),
  city: z.string().nullable(),
  countryCode: z.string().nullable(),
  /** Soundcharts' egen type: `concert`, `festival`, `online` … */
  type: z.string(),
});

export const artistSoundchartsSchema = z
  .object({
    soundchartsUuid: z.guid().nullable(),
    platforms: z.object({
      spotify: platformStatSchema,
      instagram: platformStatSchema,
      tiktok: platformStatSchema,
      youtube: platformStatSchema,
    }),
    listeners: z.object({
      series: z.array(z.object({ date: isoDate, value: z.number() })),
      current: z.number().nullable(),
      change30d: changeSchema.nullable(),
      change90d: changeSchema.nullable(),
    }),
    cities: z.object({
      snapshotDate: isoDate.nullable(),
      items: z.array(
        z.object({
          city: z.string(),
          countryCode: z.string().nullable(),
          /** Null når Soundcharts bare gir rekkefølgen. */
          listeners: z.number().nullable(),
          rank: z.number().int(),
        }),
      ),
    }),
    demographics: z.object({
      instagram: demographicsPlatformSchema,
      tiktok: demographicsPlatformSchema,
      youtube: demographicsPlatformSchema,
      /** Vektet etter rapportens følgertall per plattform (ikke likt). Null uten noen rapport. */
      combined: z
        .object({
          ages: z.array(ageShareSchema),
          genders: z.array(genderShareSchema),
          topAgeGroup: z.string().nullable(),
          countries: z.array(codeShareSchema),
        })
        .nullable(),
    }),
    followers: z.object({
      spotify: followerStatSchema,
      instagram: followerStatSchema,
      tiktok: followerStatSchema,
      youtube: followerStatSchema,
    }),
    events: z.object({
      upcoming: z.array(soundchartsEventSchema),
      past12m: z.array(soundchartsEventSchema),
    }),
    meta: z
      .object({
        biography: z.string().nullable(),
        careerStage: z.string().nullable(),
        subGenres: z.array(z.string()),
        type: z.string().nullable(),
        baseLocation: z.string().nullable(),
      })
      .nullable(),
    /** Når hver datatype sist ble hentet. */
    fetchedAt: z.record(z.string(), z.iso.datetime().nullable()),
  })
  .meta({ id: 'ArtistSoundcharts' });

export type ArtistSoundchartsDto = z.infer<typeof artistSoundchartsSchema>;
export type SoundchartsEvent = z.infer<typeof soundchartsEventSchema>;
export type SoundchartsDemographicsPlatform = z.infer<typeof demographicsPlatformSchema>;

/** Tallene artistlista sorterer på (B4), nestet på `BookingRegisterArtistDto.soundcharts`. */
export const registerSoundchartsStatsSchema = z.object({
  monthlyListeners: z.number().nullable(),
  listenersGrowth30dPct: z.number().nullable(),
  topAgeGroup: z.string().nullable(),
  instagramFollowers: z.number().nullable(),
});
export type RegisterSoundchartsStats = z.infer<typeof registerSoundchartsStatsSchema>;

// ------------------------------------------------------------------------------- Control Room

export const soundchartsSettingsSchema = z.object({
  paused: z.boolean(),
  dailyCap: z.number().int().min(0),
  firstWeekCap: z.number().int().min(0),
  /** Taket i dag: første uka etter start gjelder `firstWeekCap`. */
  effectiveCap: z.number().int().min(0),
  startedOn: isoDate.nullable(),
  quotaReserve: z.number().int().min(0),
  unlockMonthlyLimit: z.number().int().min(0),
  /** Opplåsing er stoppet ut måneden (429) eller låst (403). */
  unlockBlocked: z.boolean(),
  lockedTypes: z.array(z.enum(SOUNDCHARTS_LOCKABLE)),
  /** Dager per datatype. */
  intervals: z.record(z.string(), z.number().int()),
  updatedAt: z.iso.datetime().nullable(),
});

export const soundchartsTypeStatusSchema = z.object({
  dataType: z.enum(SOUNDCHARTS_DATA_TYPES),
  ok: z.number().int(),
  notAvailable: z.number().int(),
  locked: z.number().int(),
  error: z.number().int(),
  /** Tre feil på rad: står til noen trykker «Prøv igjen». */
  skipped: z.number().int(),
  /** I køen, aldri hentet. */
  waiting: z.number().int(),
  lastFetchAt: z.iso.datetime().nullable(),
  nextFetchAt: z.iso.datetime().nullable(),
  intervalDays: z.number().int(),
  isLocked: z.boolean(),
});

export const soundchartsOverviewSchema = z
  .object({
    /** Nøklene finnes på denne serveren. Uten dem gjøres ingen kall. */
    configured: z.boolean(),
    /** Innloggingsmåten (sak 277.1), aldri verdiene. Null når nøklene mangler. */
    authMethod: z.enum(['oauth', 'oauth_team', 'api_key']).nullable(),
    running: z.boolean(),
    settings: soundchartsSettingsSchema,
    usage: z.object({
      quotaLimit: z.number().nullable(),
      quotaUsed: z.number().nullable(),
      quotaRemaining: z.number().nullable(),
      periodEnd: z.string().nullable(),
      /** Fra siste kall (`x-quota-remaining`). */
      lastQuotaRemaining: z.number().nullable(),
      fetchedAt: z.iso.datetime().nullable(),
    }),
    callsToday: z.number().int(),
    callsThisMonth: z.number().int(),
    callsPerDay: z.array(z.object({ date: isoDate, calls: z.number().int() })),
    unlocksThisMonth: z.number().int(),
    types: z.array(soundchartsTypeStatusSchema),
    pendingSuggestions: z.number().int(),
    linkedArtists: z.number().int(),
    officialArtists: z.number().int(),
  })
  .meta({ id: 'SoundchartsOverview' });

export const updateSoundchartsSettingsRequestSchema = z
  .object({
    paused: z.boolean().optional(),
    dailyCap: z.number().int().min(0).max(100_000).optional(),
    firstWeekCap: z.number().int().min(0).max(100_000).optional(),
    quotaReserve: z.number().int().min(0).max(1_000_000).optional(),
    unlockMonthlyLimit: z.number().int().min(0).max(10_000).optional(),
    intervals: z
      .partialRecord(z.enum(SOUNDCHARTS_DATA_TYPES), z.number().int().min(1).max(365))
      .optional(),
  })
  .meta({ id: 'UpdateSoundchartsSettingsRequest' });

export const setSoundchartsTypeLockedRequestSchema = z
  .object({ dataType: z.enum(SOUNDCHARTS_LOCKABLE), locked: z.boolean() })
  .meta({ id: 'SetSoundchartsTypeLockedRequest' });

export const soundchartsScheduleEntrySchema = z.object({
  scheduleId: z.uuid(),
  dataType: z.enum(SOUNDCHARTS_DATA_TYPES),
  lastFetchAt: z.iso.datetime().nullable(),
  nextFetchAt: z.iso.datetime(),
  lastStatus: z.enum(SOUNDCHARTS_FETCH_STATUSES).nullable(),
  lastError: z.string().nullable(),
  failCount: z.number().int(),
});

export const soundchartsArtistScheduleSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    soundchartsUuid: z.guid().nullable(),
    spotifyId: z.string().nullable(),
    priority: z.number().int(),
    schedule: z.array(soundchartsScheduleEntrySchema),
  })
  .meta({ id: 'SoundchartsArtistSchedule' });

export const SOUNDCHARTS_ARTIST_SORT_KEYS = ['name'] as const;

export const listSoundchartsArtistsQuerySchema = cursorQuerySchema(SOUNDCHARTS_ARTIST_SORT_KEYS)
  .extend({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    direction: z.literal('asc').default('asc'),
    q: z.string().trim().max(100).optional(),
  })
  .meta({ id: 'ListSoundchartsArtistsQuery' });

export const soundchartsArtistScheduleListResponseSchema = page(
  soundchartsArtistScheduleSchema,
  'SoundchartsArtistScheduleListResponse',
);

export const soundchartsSuggestionSchema = z
  .object({
    id: z.uuid(),
    artistId: z.uuid(),
    artistName: z.string(),
    soundchartsUuid: z.guid(),
    name: z.string().nullable(),
    imageUrl: z.string().nullable(),
    countryCode: z.string().nullable(),
    careerStage: z.string().nullable(),
    genres: z.array(z.string()),
    rank: z.number().int(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'SoundchartsSuggestion' });

export const soundchartsSuggestionListResponseSchema = z
  .object({ items: z.array(soundchartsSuggestionSchema) })
  .meta({ id: 'SoundchartsSuggestionListResponse' });

export const decideSoundchartsSuggestionRequestSchema = z
  .object({ confirm: z.boolean() })
  .meta({ id: 'DecideSoundchartsSuggestionRequest' });

export const SOUNDCHARTS_DECISIONS = ['confirmed', 'rejected', 'uuid_taken'] as const;
export const soundchartsSuggestionDecisionSchema = z
  .object({ outcome: z.enum(SOUNDCHARTS_DECISIONS) })
  .meta({ id: 'SoundchartsSuggestionDecision' });

export const soundchartsErrorSchema = z
  .object({
    id: z.uuid(),
    occurredAt: z.iso.datetime(),
    endpoint: z.string(),
    artistId: z.uuid().nullable(),
    artistName: z.string().nullable(),
    dataType: z.string().nullable(),
    httpStatus: z.number().int(),
    status: z.string(),
    quotaRemaining: z.number().nullable(),
    error: z.string().nullable(),
  })
  .meta({ id: 'SoundchartsError' });

export const SOUNDCHARTS_ERROR_SORT_KEYS = ['recent'] as const;

export const listSoundchartsErrorsQuerySchema = cursorQuerySchema(SOUNDCHARTS_ERROR_SORT_KEYS)
  .extend({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    direction: z.literal('desc').default('desc'),
  })
  .meta({ id: 'ListSoundchartsErrorsQuery' });

export const soundchartsErrorListResponseSchema = page(
  soundchartsErrorSchema,
  'SoundchartsErrorListResponse',
);

/** Hard grense: en testkjøring tar 1–20 artister. */
export const SOUNDCHARTS_TEST_RUN_MAX = 20;

export const startSoundchartsTestRunRequestSchema = z
  .object({ artistIds: z.array(z.uuid()).min(1).max(SOUNDCHARTS_TEST_RUN_MAX) })
  .meta({ id: 'StartSoundchartsTestRunRequest' });

const testRunArtistSchema = z.object({
  artistId: z.uuid(),
  name: z.string(),
  calls: z.number().int(),
  /* Ingen kvote per artist: Soundcharts oppdaterer telleren omtrent én gang i minuttet (sak 277). */
  /** Datapunkter per datatype i denne kjøringen. */
  datapoints: z.record(z.string(), z.number().int()),
  demographics: z.record(z.string(), z.string()),
  citiesHaveCounts: z.boolean().nullable(),
  errors: z.number().int(),
});

export const soundchartsTestRunReportSchema = z.object({
  totalCalls: z.number().int(),
  quotaUsed: z.number().nullable(),
  quotaBefore: z.number().nullable(),
  quotaAfter: z.number().nullable(),
  artists: z.array(testRunArtistSchema),
  perDataType: z.array(
    z.object({
      dataType: z.string(),
      calls: z.number().int(),
      datapoints: z.number().int(),
      ok: z.number().int(),
      notAvailable: z.number().int(),
      locked: z.number().int(),
      errors: z.number().int(),
    }),
  ),
  /** Plattformene som ga en publikumsrapport, med antall artister. */
  demographicsPlatforms: z.record(z.string(), z.number().int()),
  citiesWithCounts: z.number().int(),
  citiesWithoutCounts: z.number().int(),
  unlock: z.object({
    attempts: z.number().int(),
    ok: z.number().int(),
    /** Snittet av kvote før minus etter for en vellykket opplåsing. */
    quotaCostAvg: z.number().nullable(),
    /** Opplåsinger brukt denne måneden etter kjøringen, og månedsgrensen. Mangler i eldre rapporter. */
    usedThisMonth: z.number().int().optional(),
    monthlyLimit: z.number().int().optional(),
  }),
  stoppedBy: z.string().nullable(),
  /** `metadata`: «Hent artistinfo på nytt», ett oppslag per artist. Mangler på en hel testkjøring. */
  kind: z.enum(['full', 'metadata']).optional(),
});

export const SOUNDCHARTS_TEST_RUN_STATUSES = ['running', 'succeeded', 'failed'] as const;

export const soundchartsTestRunSchema = z
  .object({
    id: z.uuid(),
    status: z.enum(SOUNDCHARTS_TEST_RUN_STATUSES),
    artistIds: z.array(z.uuid()),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    report: soundchartsTestRunReportSchema.nullable(),
  })
  .meta({ id: 'SoundchartsTestRun' });

export const soundchartsTestRunListResponseSchema = z
  .object({ items: z.array(soundchartsTestRunSchema) })
  .meta({ id: 'SoundchartsTestRunListResponse' });

/** Detaljkodene en Control Room-handling kan svare med. */
export const SOUNDCHARTS_REFUSAL_CODES = [
  'soundcharts_not_configured',
  'soundcharts_test_running',
] as const;
export type SoundchartsRefusalCode = (typeof SOUNDCHARTS_REFUSAL_CODES)[number];

export type SoundchartsSettings = z.infer<typeof soundchartsSettingsSchema>;
export type SoundchartsTypeStatus = z.infer<typeof soundchartsTypeStatusSchema>;
export type SoundchartsOverview = z.infer<typeof soundchartsOverviewSchema>;
export type UpdateSoundchartsSettingsRequest = z.infer<
  typeof updateSoundchartsSettingsRequestSchema
>;
export type SetSoundchartsTypeLockedRequest = z.infer<typeof setSoundchartsTypeLockedRequestSchema>;
export type SoundchartsScheduleEntry = z.infer<typeof soundchartsScheduleEntrySchema>;
export type SoundchartsArtistSchedule = z.infer<typeof soundchartsArtistScheduleSchema>;
export type ListSoundchartsArtistsQuery = z.infer<typeof listSoundchartsArtistsQuerySchema>;
export type SoundchartsArtistScheduleListResponse = z.infer<
  typeof soundchartsArtistScheduleListResponseSchema
>;
export type SoundchartsSuggestion = z.infer<typeof soundchartsSuggestionSchema>;
export type SoundchartsSuggestionListResponse = z.infer<
  typeof soundchartsSuggestionListResponseSchema
>;
export type DecideSoundchartsSuggestionRequest = z.infer<
  typeof decideSoundchartsSuggestionRequestSchema
>;
export type SoundchartsSuggestionDecision = z.infer<typeof soundchartsSuggestionDecisionSchema>;
export type SoundchartsError = z.infer<typeof soundchartsErrorSchema>;
export type ListSoundchartsErrorsQuery = z.infer<typeof listSoundchartsErrorsQuerySchema>;
export type SoundchartsErrorListResponse = z.infer<typeof soundchartsErrorListResponseSchema>;
export type StartSoundchartsTestRunRequest = z.infer<typeof startSoundchartsTestRunRequestSchema>;
export type SoundchartsTestRunReport = z.infer<typeof soundchartsTestRunReportSchema>;
export type SoundchartsTestRun = z.infer<typeof soundchartsTestRunSchema>;
export type SoundchartsTestRunListResponse = z.infer<typeof soundchartsTestRunListResponseSchema>;
